import React from 'react';
import ReactDOM from 'react-dom';
import 'ses';
import Chart from 'chart.js';
import fetch from 'node-fetch';
import * as FramerMotion from 'framer-motion';
import * as AicrowdUI from '../aicrowd-ui';
import * as marked from 'marked';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';
import katex from 'katex';
import rough from 'roughjs';
import regeneratorRuntime from 'regenerator-runtime';

// The single source of truth for what a premium component may import. Both
// renderers use it, so the source path and the compiled path can never drift
// into different sandboxes.
export const moduleRegistry: Record<string, unknown> = {
  react: React,
  'react-dom': ReactDOM,
  'chart.js': Chart,
  'node-fetch': fetch,
  'framer-motion': FramerMotion,
  'aicrowd-ui': AicrowdUI,
  marked,
  d3,
  'topojson-client': topojson,
  katex,
  roughjs: rough,
};

type UserComponent = React.ComponentType<Record<string, unknown>>;

/**
 * SES only protects shared intrinsics once `lockdown()` has run, and until now
 * nothing in this app ever called it.
 *
 * What that cost, measured in real Chrome (2026-09-04): a Compartment DOES hide
 * host globals it was not endowed with, and its confined `Function` cannot reach
 * the host `window` -- but it shares the host's intrinsics by identity
 * (`compartment.evaluate('Object') === Object` is true). So component code could
 * run `Object.prototype.whatever = 1` and the whole page saw it. After
 * `lockdown()` the same write throws "Cannot add property, object is not
 * extensible", and nothing else about the sandbox changes.
 *
 * Called LAZILY, immediately before the first Compartment, rather than at module
 * load or from the pack. Two reasons: a page with no premium slot never loads
 * this module and is therefore untouched, and by the time a slot mounts the
 * page's own libraries have finished whatever prototype patching they do at
 * load. Freezing earlier would be a larger blast radius for no extra protection.
 *
 * The five tamings are deliberate and are NOT the defaults:
 *   errorTaming 'unsafe'   keeps `error.stack`. SES's default redacts it, and
 *                          both renderers surface the message of a component
 *                          error to the author -- redacted stacks would make a
 *                          broken slot materially harder to diagnose.
 *   consoleTaming 'unsafe' keeps the real console rather than SES's wrapper,
 *                          which defers and re-formats output.
 *   localeTaming 'unsafe'  keeps the real toLocaleString/toLocaleDateString.
 *                          SES's default replaces the locale methods with their
 *                          non-locale counterparts, and it does so on the HOST
 *                          REALM, not just inside the Compartment -- measured in
 *                          Chrome, (1234567.5).toLocaleString('en-US') went from
 *                          "1,234,567.5" to "1234567.5" page-wide the moment a
 *                          slot mounted. That silently degrades every formatted
 *                          number on a themed challenge page, including ones
 *                          rendered by the app's own components, and
 *                          `(n ?? 0).toLocaleString()` is the house stat idiom.
 *   domainTaming 'unsafe'  is what lets this run under Jest at all. SES refuses
 *                          to lock down when Node's `domain` module has been
 *                          initialized (SES_NO_DOMAINS), and Jest initializes it
 *                          -- so with the default the call threw, the catch
 *                          below logged, and the intrinsics stayed unfrozen. The
 *                          hazard it waives is a Node confinement concern and
 *                          does not exist in a browser, which is the only place
 *                          this code ships; waiving it is what makes the
 *                          behaviour testable rather than merely asserted.
 *   overrideTaming 'severe' is what lets CodeMirror load at all, and the name
 *                          oversells it. SES's default is 'moderate', and this
 *                          file used to rely on it with a comment claiming
 *                          moderate "tolerates the override mistake in legacy
 *                          libraries". It does not tolerate the one that
 *                          matters: moderate enables overriding only
 *                          `{toString, valueOf}` on %ObjectPrototype%, and
 *                          CodeMirror 5 builds its prototype chain with
 *                          `Doc.prototype = createObj(BranchChunk.prototype,
 *                          { constructor: Doc, ... })`. BranchChunk.prototype is
 *                          an object literal, so it has no own `constructor`,
 *                          the assignment reaches the frozen
 *                          Object.prototype.constructor, and in strict mode --
 *                          which every webpack module is -- it throws
 *                          "Cannot assign to read only property 'constructor'".
 *                          That happens during module evaluation of the lazy
 *                          CodeEditor chunk, so it is not catchable by a React
 *                          error boundary: it surfaces as SES_UNCAUGHT_EXCEPTION
 *                          and unmounts the whole slot. It broke the Artifacts
 *                          viewer AND the participant-facing "write it in your
 *                          browser" editor on the submission page. Only 'severe'
 *                          enables all of %ObjectPrototype%, which SES's own
 *                          enablements.js explains is there precisely for "code
 *                          doing inheritance manually [that] will often override
 *                          the `constructor` property on the new prototype by
 *                          assignment".
 *
 *                          It is NOT a weakening of what this lockdown is for.
 *                          The enablement replaces a frozen data property with
 *                          an accessor whose setter refuses when the receiver IS
 *                          the prototype, so intrinsic POLLUTION stays blocked --
 *                          measured against ses 1.11.0, `Object.prototype.evil =
 *                          1` and overwriting constructor / toString /
 *                          Array.prototype.push all still throw under 'severe',
 *                          exactly as under 'moderate'. The only thing that
 *                          changes is that a subclass may override an inherited
 *                          property on ITSELF, which is what the override
 *                          mistake made impossible.
 *
 * Note that a FAILED lockdown still marks SES as locked down
 * (SES_ALREADY_LOCKED_DOWN on a retry), so there is no second chance -- which is
 * why the options above have to be right rather than hopeful.
 *
 * Note that a FAILED lockdown still marks SES as locked down
 * (SES_ALREADY_LOCKED_DOWN on a retry), so there is no second chance -- which is
 * why the options above have to be right rather than hopeful.
 *
 * FAIL-OPEN, and this is a real limitation rather than an oversight: if
 * lockdown() throws we log and carry on unprotected, because the alternative is
 * throwing inside a render effect, which blanks the slot. That leaves the page
 * exactly as protected as it was before this function existed -- so do not
 * describe intrinsic isolation as guaranteed; it is enforced only when this
 * succeeded.
 *
 * NOT RUN UNDER JEST, and that is a limitation of Jest rather than a choice.
 * lockdown() freezes the intrinsics of the whole REALM, and a Jest worker
 * process is reused across test FILES -- a fresh jsdom global per file, but one
 * shared Node realm. So a single file locking down breaks every suite scheduled
 * after it in that worker, with errors like "Cannot assign to read only property
 * 'toString' of function" in files that never touch this sandbox. Measured: the
 * full suite passes on a 16-core machine, where the files spread across enough
 * workers to hide it, and fails 32 of 34 suites on a smaller CI runner.
 * Reproduce locally with `yarn test:jest --maxWorkers=1`.
 *
 * The oracle for this behaviour is therefore the browser smoke scenario
 * (e2e/smoke/premium_components.spec.ts), which exercises it in real Chrome
 * where each page is its own realm -- a strictly better oracle than jsdom
 * anyway. Do not "fix" this by locking down in Jest; it will go green locally
 * and red in CI.
 */
let lockdownAttempted = false;

function ensureLockdown(): void {
  if (lockdownAttempted) return;
  lockdownAttempted = true;

  // Set only by app/javascript/jest-setup.ts. Undefined in every browser bundle,
  // so this is a no-op in production. See the comment above for why Jest cannot
  // host a lockdown.
  if ((globalThis as { __PREMIUM_SANDBOX_SKIP_LOCKDOWN__?: boolean }).__PREMIUM_SANDBOX_SKIP_LOCKDOWN__) return;

  // `harden` only exists after a successful lockdown, so it doubles as the
  // "already locked down" probe -- calling lockdown() twice throws.
  if (typeof (globalThis as { harden?: unknown }).harden === 'function') return;

  try {
    (globalThis as { lockdown?: (opts: Record<string, string>) => void }).lockdown?.({
      errorTaming: 'unsafe',
      consoleTaming: 'unsafe',
      domainTaming: 'unsafe',
      localeTaming: 'unsafe',
      overrideTaming: 'severe',
    });
  } catch (error) {
    console.error('[componentSandbox] lockdown() failed; intrinsics are NOT frozen', error);
  }
}


/**
 * Reads a host global off `window` and binds it, for the endowment table below.
 *
 * Bound, because a DOM method extracted from `window` and called bare loses its
 * receiver: real browsers throw "Illegal invocation" for `getComputedStyle(el)`
 * when the function was handed over unbound. That is the same reason the timers
 * below are bound, and it is NOT observable in Jest -- jsdom accepts an unbound
 * call -- so no test in this repo can catch a missing bind here. Bind by rule,
 * not by test result.
 *
 * Returns undefined rather than throwing when the global is absent (older
 * browsers, jsdom gaps). Endowing `undefined` leaves the component exactly as
 * badly off as it is today; letting this throw would break the module import
 * and blank EVERY slot on the page.
 */
function boundWindowFn(name: string): unknown {
  const fn = (window as unknown as Record<string, unknown>)[name];
  return typeof fn === 'function' ? (fn as (...a: unknown[]) => unknown).bind(window) : undefined;
}

/**
 * Evaluate an already-transpiled CommonJS module inside an SES Compartment and
 * return its default export. Throws if the module does not evaluate, or does
 * not default-export a function.
 *
 * The caller is responsible for having transpiled the code; this function does
 * not transform anything, which is what lets the compiled path avoid loading
 * @babel/standalone at all.
 */
export function evaluateComponentModule(code: string, allowedModules?: string[]): UserComponent {
  const moduleObj: { exports: { default?: UserComponent } } = { exports: {} };

  function customRequire(moduleName: string) {
    const allowed = allowedModules || Object.keys(moduleRegistry);

    if (!allowed.includes(moduleName)) {
      throw new Error(`Module not allowed: ${moduleName}`);
    }

    if (moduleRegistry[moduleName]) {
      return moduleRegistry[moduleName];
    }

    throw new Error(`Module not found: ${moduleName}`);
  }

  const endowments = {
    React,
    ReactDOM,
    // Babel down-levels async/await and generators into regeneratorRuntime calls
    // -- @babel/preset-env resolves .browserslistrc ("defaults"), which still
    // includes engines without native async. The pack imports
    // regenerator-runtime/runtime, so the helper exists on the PAGE, but a SES
    // Compartment gets a fresh globalThis built only from these endowments, so a
    // bare `regeneratorRuntime` reference inside a component threw
    // "ReferenceError: regeneratorRuntime is not defined" and the whole slot
    // rendered nothing.
    //
    // This affects the source path exactly as much as the compiled one -- any
    // component using async/await was broken -- so it is not a regression from
    // the server-side compile, just a gap that compiling made visible.
    regeneratorRuntime,
    document,
    console,
    window,
    navigator,
    ResizeObserver: window.ResizeObserver,
    Image: window.Image,
    setTimeout: setTimeout.bind(window),
    clearTimeout: clearTimeout.bind(window),
    setInterval: setInterval.bind(window),
    clearInterval: clearInterval.bind(window),
    requestAnimationFrame: requestAnimationFrame.bind(window),
    cancelAnimationFrame: cancelAnimationFrame.bind(window),

    // Ordinary browser globals that any rendering component reaches for. They
    // are endowed because a SES Compartment CENSORS every host global it was
    // not given -- and the way it censors them is the trap.
    //
    // Measured in Chrome and in jsdom (2026-09-04): a name that exists on the
    // real global resolves INSIDE the Compartment to `undefined`, it does not
    // throw. So `getComputedStyle` alone is harmless and `getComputedStyle(el)`
    // is "TypeError: getComputedStyle is not a function" -- a TypeError, not the
    // ReferenceError regeneratorRuntime produced. (regeneratorRuntime differs
    // because webpack scopes it to a module, so it is not a host global at all,
    // and a bare reference to it really is a ReferenceError.)
    //
    // Two consequences worth keeping in mind. A component throwing here does so
    // inside a render effect with no error boundary, so the slot mounts nothing
    // and the page shows a blank tab rather than an error. And a test that only
    // REFERENCES one of these passes on the unfixed tree -- an oracle has to
    // CALL them; see __tests__/sandboxEndowments.spec.tsx.
    //
    // `window` is already endowed, so none of this widens what a component can
    // reach: `window.getComputedStyle` has always worked. It removes a trap for
    // the author who writes the bare form, which is the form every example on
    // the web uses.
    getComputedStyle: boundWindowFn('getComputedStyle'),
    getSelection: boundWindowFn('getSelection'),
    matchMedia: boundWindowFn('matchMedia'),
    fetch: boundWindowFn('fetch'),
    // Objects and constructors, so no bind: a constructor must stay callable
    // with `new`, and Storage/Performance methods are already bound to their
    // own instance.
    localStorage: window.localStorage,
    sessionStorage: window.sessionStorage,
    performance: window.performance,
    IntersectionObserver: window.IntersectionObserver,
    MutationObserver: window.MutationObserver,
    URL: window.URL,
    // A component reading `location.pathname` to build a link is the single most
    // common thing that broke here, and it broke SILENTLY: censored to undefined,
    // so the read throws a TypeError inside a render effect and the slot mounts
    // nothing. The WhestBench theme carries a documented workaround for exactly
    // this -- its DEPLOYMENT.md records dead "View" links on the Leaderboard and
    // Submissions slots, fixed by routing through `window.location` because a bare
    // `location` was not provided.
    //
    // Endowed as the Location OBJECT, not bound: its methods (assign, replace,
    // reload) are already bound to it, and `window` was always endowed, so this
    // grants a component no capability it did not already have through
    // `window.location`. It removes a trap, not a restriction.
    location: window.location,
    // ECMA-402, not ECMA-262: SES's intrinsics are 262 only, so `Intl` is
    // censored like any DOM global even though it feels like a language
    // built-in. Charts and dashboards format numbers and dates with it.
    Intl: window.Intl,
    // Date and Math are ECMA-262 intrinsics, so a Compartment gets them without
    // being endowed -- but after lockdown() it gets SES's POWERLESS versions, and
    // the powerless ones throw on exactly the members components use:
    //
    //   Date.now()      -> "secure mode Calling %SharedDate%.now() throws"
    //   new Date()      -> throws (the ZERO-ARG form only; new Date(0) is fine)
    //   Math.random()   -> "secure mode %SharedMath%.random() throws"
    //
    // Measured in real Chrome, ses 1.11, with this file's exact lockdown options.
    // SES removes ambient access to the clock and to entropy by design; endowing
    // the host's Date and Math hands both back, which is correct here because a
    // premium component is admin-authored and already gets `window`.
    //
    // Not endowing them broke SEVEN shipped starters -- the copy-paste examples --
    // including Banner/02_CountdownTimer and Overview/05_LiveActivityFeed, which
    // call Date.now() at MODULE TOP LEVEL, so compartment.evaluate itself throws
    // and PrecompiledComponentRenderer falls back to the default Rails content.
    // A themed countdown banner would simply have reverted, silently, in
    // production.
    Date: window.Date,
    Math: window.Math,

    require: customRequire,
    module: moduleObj,
    exports: moduleObj.exports,
  };

  // Before the first Compartment: freezes the intrinsics this Compartment will
  // share with the host page. See ensureLockdown.
  ensureLockdown();

  const compartment = new Compartment(endowments);
  compartment.evaluate(`${code}\n//# sourceURL=userModule.js`);

  const DynamicComponent = moduleObj.exports.default;

  if (typeof DynamicComponent !== 'function') {
    throw new Error('No valid default React component exported.');
  }

  return DynamicComponent;
}

/**
 * SES rejects a script containing an HTML comment token before it evaluates it.
 * The source path scrubs the token so a component with `-->` inside a string
 * still renders; note this CORRUPTS a regex literal containing the same token,
 * which is why bin/compile-component.js refuses to emit one at all rather than
 * scrubbing it.
 */
export function scrubHtmlCommentTokens(code: string): string {
  return code.replace(/<!--/g, '< !--').replace(/-->/g, '-- >');
}
